import React, { useState, useRef } from 'react';
import { notes } from '../api';

// StudentCard component with drag-drop support
function StudentCard({ player, onView, onRemove, onUpdate, onLongTap }) {
  const [isDragging, setIsDragging] = useState(false);
  const longPressTimeoutRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleCardTouchStart = () => {
    longPressTimeoutRef.current = setTimeout(() => {
      if (onLongTap) {
        onLongTap(player._id, player);
      }
    }, 500); // 500ms long press threshold
  };

  const handleCardTouchEnd = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
    }
  };

  const handleCardTouchMove = () => {
    // Cancel long press if user drags
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const lessonId = e.dataTransfer.getData('text/plain');
    
    if (window.receiveLessonDrop) {
      const lessonNote = window.receiveLessonDrop(lessonId);
      
      if (lessonNote) {
        try {
          // Add note to this student
          await notes.addNote(player._id, lessonNote.text);
          
          // Success feedback
          e.currentTarget.style.backgroundColor = '#27ae60';
          e.currentTarget.style.transform = 'scale(1.05)';
          
          setTimeout(async () => {
            e.currentTarget.style.backgroundColor = '';
            e.currentTarget.style.transform = '';
            
            // Call update function to reload players without page refresh
            if (onUpdate) {
              await onUpdate();
            }
          }, 800);
        } catch (error) {
          console.error('Error adding lesson note:', error);
          
          // Only show alert if it actually failed (not a CORS or network issue)
          if (error.response?.status >= 400) {
            alert('Failed to add lesson note: ' + (error.response?.data?.message || error.message));
          } else {
            // It probably worked despite the error, so just update
            setTimeout(async () => {
              e.currentTarget.style.backgroundColor = '';
              e.currentTarget.style.transform = '';
              if (onUpdate) {
                await onUpdate();
              }
            }, 800);
          }
        }
      }
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onTouchStart={handleCardTouchStart}
      onTouchEnd={handleCardTouchEnd}
      onTouchMove={handleCardTouchMove}
      onClick={(e) => {
        // Only open if not clicking remove button
        if (!e.target.closest('.remove-btn')) {
          onView(player);
        }
      }}
      className="student-list-card"
      style={{
        background: isDragging ? '#e3f2fd' : 'white',
        padding: '20px',
        borderRadius: '8px',
        cursor: 'pointer',
        boxShadow: isDragging ? '0 8px 16px rgba(52, 152, 219, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        position: 'relative',
        border: isDragging ? '3px dashed #3498db' : '3px solid transparent',
        transform: isDragging ? 'scale(1.02)' : 'scale(1)'
      }}
    >
      {isDragging && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '48px',
          pointerEvents: 'none'
        }}>
          📝
        </div>
      )}
      
      <button
        className="remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          if (window.confirm(`Remove ${player.name}? Their notes will be deleted.`)) {
            onRemove(player._id);
          }
        }}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: '#e74c3c',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          cursor: 'pointer',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isDragging ? 0.3 : 1,
          transition: 'all 0.2s'
        }}
      >
        ×
      </button>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>{player.name}</h3>
        {player.gender && (
          <span title={player.gender === 'f' ? 'Female' : player.gender === 'm' ? 'Male' : 'Other'}>
            {player.gender === 'f' ? '♀️' : player.gender === 'm' ? '♂️' : '⚪'}
          </span>
        )}
        {player.handedness && (
          <span title={player.handedness === 'l' ? 'Left-handed' : 'Right-handed'}>
            {player.handedness === 'l' ? '🫲' : '🫱'}
          </span>
        )}
        {player.role === 'manager' && (
          <span style={{
            backgroundColor: '#fff3e0',
            color: '#e65100',
            padding: '2px 8px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 'bold'
          }}>
            👑 Manager
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: '14px', color: '#7f8c8d' }}>{player.email}</p>
      
      {player.noteCount !== undefined && (
        <div style={{
          marginTop: '12px',
          padding: '6px 12px',
          background: player.noteCount > 0 ? '#3498db' : '#95a5a6',
          color: 'white',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          display: 'inline-block'
        }}>
          {player.noteCount} {player.noteCount === 1 ? 'note' : 'notes'}
        </div>
      )}
    </div>
  );
}

export default StudentCard;